import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Chip from '@mui/material/Chip'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import FeedbackIcon from '@mui/icons-material/FeedbackOutlined'
import CloseIcon from '@mui/icons-material/Close'
import BugReportIcon from '@mui/icons-material/BugReport'
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import ThumbDownIcon from '@mui/icons-material/ThumbDown'
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral'
import { submitFeedback } from '../services/feedbackService'
import inputController, { InputLockReason } from '../services/InputController'
import { useAppSelector } from '../hooks'
import { usePermissionStore } from '../stores/permissionStore'
import { zIndexLayers } from '../MuiTheme'
import { useHoldCompanyAccessReload } from '../hooks/companyAccessReloadHold'
import { BOTTOM_RIGHT_EDGE, FEEDBACK_RIGHT, TOGGLE_SIZE } from './bottomRightControls'

const MAX_MESSAGE_LENGTH = 1000
const MAX_RATING_COMMENT_LENGTH = 500

const DRAWER_WIDTH = 380
const DRAWER_HEIGHT = 480

const DrawerWrapper = styled.div<{ open: boolean }>`
  position: fixed;
  right: ${FEEDBACK_RIGHT}px;
  bottom: ${BOTTOM_RIGHT_EDGE}px;
  width: ${({ open }) => (open ? `${DRAWER_WIDTH}px` : `${TOGGLE_SIZE}px`)};
  height: ${({ open }) => (open ? `${DRAWER_HEIGHT}px` : `${TOGGLE_SIZE}px`)};
  ${({ open }) => open && 'max-height: 85vh; min-height: 320px;'}
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: ${zIndexLayers.modals};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`

const DrawerPanel = styled.div<{ open: boolean }>`
  display: ${({ open }) => (open ? 'flex' : 'none')};
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: linear-gradient(145deg, rgba(16, 20, 31, 0.98), rgba(26, 32, 44, 0.95));
  border: 1px solid #f97415;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(249, 116, 21, 0.15);
  overflow: hidden;
  backdrop-filter: blur(10px);
  color: #fff;
`

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background-color: rgba(0, 0, 0, 0.2);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`

const DrawerContent = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
`

const ToggleButton = styled(IconButton)`
  width: ${TOGGLE_SIZE}px !important;
  height: ${TOGGLE_SIZE}px !important;
  background: rgba(30, 34, 50, 0.7) !important;
  color: #f97415 !important;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(40, 44, 65, 0.95) !important;
    border-color: rgba(249, 116, 21, 0.35) !important;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
`

const StarButton = styled(IconButton)`
  padding: 4px !important;
  color: rgba(255, 255, 255, 0.3) !important;
  transition: all 0.15s ease;

  &.filled {
    color: #f97415 !important;
  }

  &:hover {
    color: #f97415 !important;
    transform: scale(1.15);
  }
`

const chipSx = {
  color: 'rgba(255, 255, 255, 0.9)',
  backgroundColor: 'rgba(30, 34, 50, 0.7)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(40, 44, 65, 0.95)',
    borderColor: 'rgba(249, 116, 21, 0.35)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
  },
} as const

/**
 * Feedback drawer at bottom-right.
 * Toggle button expands to show a 2-step form (feature request / bug report) inline.
 */
type FeedbackType = 'bug' | 'feature' | 'rating'
type WouldRecommend = 'yes' | 'maybe' | 'no'

const RATING_LABELS = ['Poor', 'Fair', 'Good', 'Very good', 'Excellent']

export default function FeedbackDrawer() {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<1 | 2>(1)
  const [type, setType] = useState<FeedbackType | null>(null)
  const [message, setMessage] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [errorSnackbar, setErrorSnackbar] = useState<string | null>(null)
  const [starRating, setStarRating] = useState<number>(0)
  const [wouldRecommend, setWouldRecommend] = useState<WouldRecommend | null>(null)

  const authenticated = useAppSelector((state) => state.user.authenticated)
  // Platform admins don't get the floating toggle; the menu's Leave Feedback still opens the drawer.
  const isPlatformAdmin = usePermissionStore(
    (s) => s.adminCapabilitiesVerified && (s.isGlobalAdmin || s.isPlatformAdmin),
  )
  // Leave Feedback stays open to suspended companies; a reactivation reload would drop the draft.
  useHoldCompanyAccessReload(open)

  useEffect(() => {
    const handler = () => setOpen(true)
    window.addEventListener('openFeedbackDrawer', handler)
    return () => window.removeEventListener('openFeedbackDrawer', handler)
  }, [])

  useEffect(() => {
    if (open) {
      inputController.lockInput(InputLockReason.FEEDBACK_DRAWER)
    } else {
      inputController.unlockInput(InputLockReason.FEEDBACK_DRAWER)
    }
    return () => inputController.unlockInput(InputLockReason.FEEDBACK_DRAWER)
  }, [open])

  const handleClose = () => {
    setOpen(false)
    setStep(1)
    setType(null)
    setMessage('')
    setStarRating(0)
    setWouldRecommend(null)
    setSubmitting(false)
    setErrorSnackbar(null)
  }

  const handleToggle = () => {
    if (open) {
      handleClose()
    } else {
      setOpen(true)
      setStep(1)
      setType(null)
      setMessage('')
      setStarRating(0)
      setWouldRecommend(null)
    }
  }

  const handleTypeSelect = (selected: FeedbackType) => {
    setType(selected)
    setStep(2)
    setMessage('')
    if (selected !== 'rating') {
      setStarRating(0)
      setWouldRecommend(null)
    }
  }

  const handleBack = () => {
    setStep(1)
    setType(null)
    setMessage('')
    setStarRating(0)
    setWouldRecommend(null)
  }

  const canSubmitRating = type === 'rating' && starRating >= 1 && wouldRecommend !== null
  const canSubmitOther = type && type !== 'rating' && message.trim().length > 0

  const handleSubmit = async () => {
    if (type === 'rating') {
      if (!canSubmitRating) return
      setSubmitting(true)
      setErrorSnackbar(null)
      try {
        const result = await submitFeedback({
          type: 'rating',
          starRating,
          wouldRecommend: wouldRecommend!,
          message: message.trim(),
        })
        if (result.success) {
          handleClose()
        } else {
          setErrorSnackbar(result.error || 'Failed to send feedback')
        }
      } catch (err: any) {
        setErrorSnackbar(err?.message || 'Something went wrong')
      } finally {
        setSubmitting(false)
      }
    } else {
      if (!canSubmitOther) return
      setSubmitting(true)
      setErrorSnackbar(null)
      try {
        const result = await submitFeedback({
          type: type!,
          message: message.trim(),
        })
        if (result.success) {
          handleClose()
        } else {
          setErrorSnackbar(result.error || 'Failed to send feedback')
        }
      } catch (err: any) {
        setErrorSnackbar(err?.message || 'Something went wrong')
      } finally {
        setSubmitting(false)
      }
    }
  }

  const placeholder =
    type === 'bug'
      ? 'What went wrong? Describe the issue...'
      : type === 'feature'
        ? 'Describe your idea or feature request...'
        : "Any additional thoughts? (optional)"

  if (!authenticated) {
    return null
  }

  return (
    <>
      <DrawerWrapper open={open}>
        <DrawerPanel open={open}>
          <DrawerHeader>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <FeedbackIcon sx={{ color: '#f97415', fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                Leave Feedback
              </Typography>
            </Box>
            <IconButton
              aria-label="Close feedback"
              onClick={handleToggle}
              size="small"
                sx={{
                  color: 'rgba(255,255,255,0.7)',
                  '&:hover': {
                    color: '#f97415',
                    backgroundColor: 'rgba(249, 116, 21, 0.1)',
                  },
                }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DrawerHeader>
          <DrawerContent>
            {step === 1 && (
              <Box>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 2 }}>
                  What would you like to share?
                </Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Chip
                    icon={<BugReportIcon sx={{ color: '#f97415 !important' }} />}
                    label="Bug Report"
                    onClick={() => handleTypeSelect('bug')}
                    sx={chipSx}
                  />
                  <Chip
                    icon={<LightbulbOutlinedIcon sx={{ color: '#f97415 !important' }} />}
                    label="Feature Request"
                    onClick={() => handleTypeSelect('feature')}
                    sx={chipSx}
                  />
                  <Chip
                    icon={<StarIcon sx={{ color: '#f97415 !important' }} />}
                    label="Rate the App"
                    onClick={() => handleTypeSelect('rating')}
                    sx={chipSx}
                  />
                </Box>
              </Box>
            )}
            {step === 2 && type === 'rating' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 2.5 }}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1.5 }}>
                    How would you rate your experience?
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <StarButton
                        key={n}
                        onClick={() => setStarRating(n)}
                        className={n <= starRating ? 'filled' : ''}
                        aria-label={`${n} star${n > 1 ? 's' : ''}`}
                      >
                        {n <= starRating ? (
                          <StarIcon sx={{ fontSize: 36 }} />
                        ) : (
                          <StarBorderIcon sx={{ fontSize: 36 }} />
                        )}
                      </StarButton>
                    ))}
                  </Box>
                  {starRating > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ color: '#f97415', fontWeight: 600, display: 'block', mt: 0.5 }}
                    >
                      {RATING_LABELS[starRating - 1]}
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1.5 }}>
                    Would you recommend Sevv to a friend or colleague?
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      icon={<ThumbUpIcon sx={{ color: wouldRecommend === 'yes' ? '#4caf50' : '#f97415', fontSize: 18 }} />}
                      label="Yes"
                      onClick={() => setWouldRecommend('yes')}
                      sx={{
                        ...chipSx,
                        borderColor: wouldRecommend === 'yes' ? '#4caf50' : undefined,
                        backgroundColor: wouldRecommend === 'yes' ? 'rgba(76, 175, 80, 0.2)' : undefined,
                      }}
                    />
                    <Chip
                      icon={<SentimentNeutralIcon sx={{ color: '#f97415', fontSize: 18 }} />}
                      label="Maybe"
                      onClick={() => setWouldRecommend('maybe')}
                      sx={{
                        ...chipSx,
                        borderColor: wouldRecommend === 'maybe' ? '#f97415' : undefined,
                        backgroundColor: wouldRecommend === 'maybe' ? 'rgba(249, 116, 21, 0.2)' : undefined,
                      }}
                    />
                    <Chip
                      icon={<ThumbDownIcon sx={{ color: wouldRecommend === 'no' ? '#f44336' : '#f97415', fontSize: 18 }} />}
                      label="No"
                      onClick={() => setWouldRecommend('no')}
                      sx={{
                        ...chipSx,
                        borderColor: wouldRecommend === 'no' ? '#f44336' : undefined,
                        backgroundColor: wouldRecommend === 'no' ? 'rgba(244, 67, 54, 0.2)' : undefined,
                      }}
                    />
                  </Box>
                </Box>
                <Box sx={{ flex: 1, minHeight: 80 }}>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mb: 1 }}>
                    Anything else you&apos;d like to share? (optional)
                  </Typography>
                  <TextField
                    multiline
                    minRows={2}
                    maxRows={6}
                    placeholder="Share your thoughts..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value.slice(0, MAX_RATING_COMMENT_LENGTH))}
                    fullWidth
                    disabled={submitting}
                    inputProps={{ maxLength: MAX_RATING_COMMENT_LENGTH, style: { resize: 'none' } }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(30, 34, 50, 0.7)',
                        borderRadius: 2,
                        alignItems: 'flex-start',
                        '& .MuiInputBase-input': {
                          color: 'rgba(255, 255, 255, 0.9)',
                          overflowY: 'auto !important',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          '&::placeholder': { color: 'rgba(255, 255, 255, 0.5)' },
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(255, 255, 255, 0.15)',
                            borderRadius: '3px',
                          },
                          '&::-webkit-scrollbar-thumb:hover': {
                            background: 'rgba(255, 255, 255, 0.25)',
                          },
                        },
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                        '&:hover fieldset': { borderColor: 'rgba(249, 116, 21, 0.35)' },
                        '&.Mui-focused fieldset': { borderColor: '#f97415' },
                      },
                    }}
                  />
                  {message.length > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', mt: 0.5, flexShrink: 0 }}>
                  {message.length} / {MAX_RATING_COMMENT_LENGTH}
                </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 1, flexShrink: 0 }}>
                  <Button
                    onClick={handleBack}
                    disabled={submitting}
                    variant="outlined"
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        borderColor: '#f97415',
                        backgroundColor: 'rgba(249, 116, 21, 0.1)',
                      },
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || !canSubmitRating}
                    sx={{
                      backgroundColor: '#f97415',
                      color: 'white',
                      '&:hover': { backgroundColor: '#ffa047' },
                      '&:disabled': {
                        backgroundColor: 'rgba(249, 116, 21, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {submitting ? 'Sending...' : 'Submit Rating'}
                  </Button>
                </Box>
              </Box>
            )}
            {step === 2 && type && type !== 'rating' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, gap: 1.5 }}>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', flexShrink: 0 }}>
                  {type === 'bug' ? 'Describe what went wrong' : 'Describe your idea'}
                </Typography>
                <TextField
                  multiline
                  minRows={5}
                  maxRows={12}
                  placeholder={placeholder}
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
                  fullWidth
                  autoFocus
                  disabled={submitting}
                  inputProps={{ maxLength: MAX_MESSAGE_LENGTH, style: { resize: 'none' } }}
                  sx={{
                    flex: 1,
                    minHeight: 140,
                    display: 'flex',
                    flexDirection: 'column',
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: 'rgba(30, 34, 50, 0.7)',
                      borderRadius: 2,
                      alignItems: 'stretch',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      flex: 1,
                      '& .MuiInputBase-input': {
                        color: 'rgba(255, 255, 255, 0.9)',
                        height: '100% !important',
                        overflowY: 'auto !important',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        '&::placeholder': { color: 'rgba(255, 255, 255, 0.5)' },
                        '&::-webkit-scrollbar': {
                          width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'rgba(0, 0, 0, 0.1)',
                          borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(255, 255, 255, 0.15)',
                          borderRadius: '3px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: 'rgba(255, 255, 255, 0.25)',
                        },
                      },
                      '& fieldset': {
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'rgba(249, 116, 21, 0.35)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#f97415',
                      },
                    },
                  }}
                />
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', flexShrink: 0 }}>
                  {message.length} / {MAX_MESSAGE_LENGTH}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 2, flexShrink: 0 }}>
                  <Button
                    onClick={handleBack}
                    disabled={submitting}
                    variant="outlined"
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        borderColor: '#f97415',
                        backgroundColor: 'rgba(249, 116, 21, 0.1)',
                      },
                      '&:disabled': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        borderColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting || !canSubmitOther}
                    sx={{
                      backgroundColor: '#f97415',
                      color: 'white',
                      '&:hover': { backgroundColor: '#ffa047' },
                      '&:disabled': {
                        backgroundColor: 'rgba(249, 116, 21, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                    }}
                  >
                    {submitting ? 'Sending...' : 'Send Feedback'}
                  </Button>
                </Box>
              </Box>
            )}
          </DrawerContent>
        </DrawerPanel>
        {!isPlatformAdmin && (
          <Tooltip title={open ? 'Close' : 'Leave Feedback'} arrow placement="left">
            <ToggleButton
              onClick={handleToggle}
              aria-label={open ? 'Close feedback' : 'Leave feedback'}
              size="medium"
            >
              <FeedbackIcon fontSize="small" />
            </ToggleButton>
          </Tooltip>
        )}
      </DrawerWrapper>

      <Snackbar
        open={!!errorSnackbar}
        autoHideDuration={6000}
        onClose={() => setErrorSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorSnackbar(null)} variant="filled">
          {errorSnackbar}
        </Alert>
      </Snackbar>
    </>
  )
}
